import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReviewOrder = ({ onNext, onBack, formData, setFormData }) => {
  const [giftMessage, setGiftMessage] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!agreeTerms) {
      setErrors({ terms: 'You must agree to the terms and conditions' });
      return;
    }

    setFormData(prev => ({
      ...prev,
      giftMessage,
      subscribeNewsletter,
      agreeTerms
    }));
    
    onNext();
  };

  const paymentMethodDisplay = {
    card: 'Credit/Debit Card',
    paypal: 'PayPal',
    apple: 'Apple Pay',
    google: 'Google Pay'
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
          <Icon name="Eye" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Review Your Order</h2>
          <p className="text-sm text-muted-foreground">Verify your details before placing the order</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Shipping Information Review */}
        <div className="p-4 bg-background/50 rounded-lg border border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-foreground flex items-center">
              <Icon name="MapPin" size={16} className="mr-2 text-primary" />
              Shipping Address
            </h3>
            <Button variant="ghost" size="sm" onClick={() => onBack(1)}>
              <Icon name="Edit2" size={14} className="mr-1" />
              Edit
            </Button>
          </div>
          <div className="text-sm text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">{formData?.firstName} {formData?.lastName}</p>
            <p>{formData?.address}</p>
            <p>{formData?.city}, {formData?.state} {formData?.zipCode}</p>
            <p>{formData?.country}</p>
            <p className="flex items-center mt-2">
              <Icon name="Phone" size={14} className="mr-2" />
              {formData?.phone}
            </p>
            <p className="flex items-center">
              <Icon name="Mail" size={14} className="mr-2" />
              {formData?.email}
            </p>
          </div>
        </div>

        {/* Payment Information Review */}
        <div className="p-4 bg-background/50 rounded-lg border border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-foreground flex items-center">
              <Icon name="CreditCard" size={16} className="mr-2 text-primary" />
              Payment Method
            </h3>
            <Button variant="ghost" size="sm" onClick={() => onBack(2)}>
              <Icon name="Edit2" size={14} className="mr-1" />
              Edit
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground">
              {paymentMethodDisplay?.[formData?.paymentMethod] || 'Credit/Debit Card'}
            </p>
            {formData?.paymentMethod === 'card' && formData?.cardNumber && (
              <p>**** **** **** {formData?.cardNumber?.slice(-4)}</p>
            )}
          </div>
        </div>

        {/* Delivery Options */}
        <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
          <h3 className="font-medium text-foreground flex items-center mb-3">
            <Icon name="Truck" size={16} className="mr-2 text-primary" />
            Delivery Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={14} className="text-primary" />
              <span className="text-muted-foreground">Estimated Delivery:</span>
              <span className="font-medium text-foreground">Sep 29, 2025</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Package" size={14} className="text-primary" />
              <span className="text-muted-foreground">Shipping Method:</span>
              <span className="font-medium text-foreground">Standard (Free)</span>
            </div>
          </div>
        </div>

        {/* Gift Message */}
        <div className="space-y-3">
          <h3 className="font-medium text-foreground flex items-center">
            <Icon name="Gift" size={16} className="mr-2 text-accent" />
            Gift Message (Optional)
          </h3>
          <Input
            type="text"
            placeholder="Add a personal message for the recipient..."
            value={giftMessage}
            onChange={(e) => setGiftMessage(e?.target?.value)}
            description="Maximum 200 characters"
          />
        </div>

        {/* Terms and Conditions */}
        <div className="space-y-4 pt-4 border-t border-border">
          <Checkbox
            label={`I agree to the Terms of Service and Privacy Policy`}
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e?.target?.checked)}
            error={errors?.terms}
            required
          />
          
          <Checkbox
            label="Subscribe to newsletter for exclusive deals and updates"
            checked={subscribeNewsletter}
            onChange={(e) => setSubscribeNewsletter(e?.target?.checked)}
          />
        </div>

        {/* Order Confirmation Notice */}
        <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-warning mb-1">Order Confirmation</p>
              <p className="text-muted-foreground">
                By placing this order, you confirm that all information is accurate. 
                You will receive an email confirmation with tracking details within 24 hours.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-6">
          <Button variant="outline" onClick={() => onBack(2)}>
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to Payment
          </Button>
          <Button type="submit" variant="default" className="px-8 bg-success hover:bg-success/90">
            <Icon name="CheckCircle" size={16} className="mr-2" />
            Place Order
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ReviewOrder;